import React, { useEffect, useState } from 'react';
import { getResourceBySlug } from '../../../lib/db';
import PlaybookSection from '../../../components/playbooks/PlaybookSection';
import PlaybookHero from '../../../components/playbooks/PlaybookHero';
import PlaybookMetrics from '../../../components/playbooks/PlaybookMetrics';
import VideoEmbed from '../../../components/playbooks/VideoEmbed';
import ExpandableCallout from '../../../components/playbooks/ExpandableCallout';
import { 
  InformationCircleIcon,
  PlayCircleIcon,
  AcademicCapIcon,
  WrenchScrewdriverIcon,
  ExclamationTriangleIcon,
  EyeIcon,
  UserGroupIcon,
  RocketLaunchIcon,
  SparklesIcon,
  DocumentTextIcon
} from '@heroicons/react/24/outline';

const EmployerBrandKeyInfo = () => {
  const [resource, setResource] = useState(null);
  const metrics = [
    {
      type: 'timeline',
      title: 'Timeline',
      value: '4-6 weeks'
    },
    {
      type: 'size',
      title: 'Team Size',
      value: '1-3 people'
    },
    {
      type: 'why',
      title: 'Why Use',
      value: 'Build an authentic, transparent employer brand that attracts and repels the right talent'
    },
    {
      type: 'when',
      title: 'When to Use',
      value: 'When you want to move away from formulaic recruitment approaches and build a more honest, open organization'
    }
  ];

  useEffect(() => {
    getResourceBySlug('employer-brand-unfiltered').then(setResource);
  }, []);

  return (
    <div>
      <PlaybookHero
        title={resource?.title || "Unfiltered: The Startup Employer Brand Playbook"}
        subtitle={resource?.description || "Building an authentic, transparent employer brand that attracts and repels the right talent"}
        category={resource?.category || "People & Culture"}
        bannerUrl={resource?.banner_url || "/images/employer-brand-banner.jpg"}
        readingTime={resource?.timeframe || "45 min read"}
        type={resource?.type}
      />
      <div id="key-info" className="prose prose-invert max-w-none">
        <h2 className="text-2xl font-bold mb-6 flex items-center">
          <InformationCircleIcon className="h-6 w-6 text-primary-light mr-2" />
          Key Info
        </h2>

        {/* This Guide is for you section */}
        <h3 className="text-xl font-semibold mb-4">This Guide is for you if you can't answer these questions:</h3>
        <ul className="space-y-3 mb-8">
          <li className="flex items-start">
            <span className="text-primary-light mr-3">•</span>
            <span>What's the top 3 reasons people love working in our company?</span>
          </li>
          <li className="flex items-start">
            <span className="text-primary-light mr-3">•</span>
            <span>Whats the 3 biggest reasons someone shouldn't join our company?</span>
          </li>
          <li className="flex items-start">
            <span className="text-primary-light mr-3">•</span>
            <span>Who thrives in our business, and why?</span>
          </li>
          <li className="flex items-start">
            <span className="text-primary-light mr-3">•</span>
            <span>Who doesn't, and why?</span>
          </li>
        </ul>

        {/* Guide Overview */}
        <h3 className="text-xl font-semibold mb-4">Guide Overview</h3>
        <div className="mb-12">
          <PlaybookMetrics metrics={metrics} />
        </div>

        {/* Watch This section */}
        <div className="mb-10 p-6 border border-gray-800 rounded-lg bg-gray-900/50">
          <div className="flex items-center mb-4">
            <PlayCircleIcon className="h-7 w-7 text-primary-light mr-3" />
            <h3 className="text-xl font-semibold">Watch This</h3>
          </div>
          <VideoEmbed
            src="https://www.loom.com/embed/9f5fffb5b58a444dab3337fa56724f71"
            title="Introduction to Unfiltered Employer Branding"
          />
        </div>

        {/* What Is This Content */}
        <div className="mb-10 p-6 border border-gray-800 rounded-lg bg-gray-900/50">
          <div className="flex items-center mb-4">
            <AcademicCapIcon className="h-7 w-7 text-primary-light mr-3" />
            <h3 className="text-xl font-semibold">What Is This</h3>
          </div>
          <div className="space-y-4">
            <p className="text-lg">
              Two well-shared statistics you should bear in mind as you embark on reading through this playbook:
            </p>
            <div className="p-4 border-l-4 border-primary-light bg-gray-900/30 rounded-r-lg">
              <ul className="space-y-2">
                <li className="flex items-start">
                  <span className="text-primary-light mr-2">•</span>
                  <span>4 in 10 Hiring Managers have admitted to lying to candidates in interview. (ResumeBuilder)</span>
                </li>
                <li className="flex items-start">
                  <span className="text-primary-light mr-2">•</span>
                  <span>33% of new hires leave within 90 days of starting a role, citing culture fit as the main reason. (Jobvite)</span>
                </li>
              </ul>
            </div>
            <p className="text-lg">
              The companies that contribute to these statistics are wasting time, money, energy, and brand reputation by mis-selling their culture & offering to talent.
            </p>
            <div className="space-y-2">
              <p className="text-lg flex items-start">
                <span className="text-red-500 mr-2">×</span>
                <span>Stop using the formulaic, off-the-shelf sales tactics used by companies to fuel the ongoing 'war for talent'.</span>
              </p>
              <p className="text-lg flex items-start">
                <span className="text-green-500 mr-2">✓</span>
                <span>Start building a more authentic, honest, open org principled on bringing it's whole self to the hiring process, not some shiny, ultra-processed, highly-manufactured version that causes more damage than good to everyone involved.</span>
              </p>
            </div>

            {/* Employer Brand Image */}
            <div className="mt-8">
              <img 
                src="https://auth.openorg.fyi/storage/v1/object/public/resources/employer-brand-unfiltered/openorg-employer-brand.jpg"
                alt="How We See Employer Brand at Open Org"
                className="rounded-lg w-full"
              />
              <p className="text-gray-400 text-sm mt-2 italic">How We See Employer Brand at Open Org</p>
            </div>
          </div>
        </div>

        {/* What You'll Need Content */}
        <div className="mb-10 p-6 border border-gray-800 rounded-lg bg-gray-900/50">
          <div className="flex items-center mb-4">
            <WrenchScrewdriverIcon className="h-7 w-7 text-primary-light mr-3" />
            <h3 className="text-xl font-semibold">Open Org Principles</h3>
          </div>
          <div className="space-y-4">
            <p className="text-lg">
              The playbook & guidance below has been built with the following beliefs in mind…
            </p>
            <ul className="space-y-6">
              <li className="flex items-start">
                <EyeIcon className="h-6 w-6 text-primary-light mr-3 mt-1" />
                <div>
                  <p className="font-semibold">Leverage Transparency</p>
                  <p>to attract AND repel the right talent. Show your WHOLE EVP & Culture. Your Employer Brand should be an unedited view into your culture, not a separate product you build.</p>
                </div>
              </li>
              <li className="flex items-start">
                <UserGroupIcon className="h-6 w-6 text-primary-light mr-3 mt-1" />
                <div>
                  <p className="font-semibold">Think like a product marketer</p>
                  <p>understand your customer, and help them make a fully formed decision about whether to subscribe to, and keep subscribing to your product with minimal churn.</p>
                </div>
              </li>
              <li className="flex items-start">
                <RocketLaunchIcon className="h-6 w-6 text-primary-light mr-3 mt-1" />
                <div>
                  <p className="font-semibold">Your product is your EVP</p>
                  <p>it goes far beyond "benefits"… Think culture & ways of work, pay, progression, growth, flexibility</p>
                </div>
              </li>
              <li className="flex items-start">
                <SparklesIcon className="h-6 w-6 text-primary-light mr-3 mt-1" />
                <div>
                  <p className="font-semibold">Authenticity Wins, Every Time</p>
                  <p>Better to be open about where you're at and what you're doing about it vs concealing the truth…they'll find out in interview/onboarding anyway…#poorCX</p>
                </div>
              </li>
            </ul>
          </div>
        </div>

        {/* Common Obstacles */}
        <div className="mb-10 p-6 border border-gray-800 rounded-lg bg-gray-900/50">
          <div className="flex items-center mb-4">
            <ExclamationTriangleIcon className="h-7 w-7 text-primary-light mr-3" />
            <h3 className="text-xl font-semibold">Common Obstacles</h3>
          </div>
          <div className="space-y-4">
            <ExpandableCallout
              type="warning"
              title="Fear of Being Too Transparent"
              defaultOpen={true}
            >
              <p className="text-gray-300">
                Many companies worry that being too transparent about their culture and challenges will deter candidates. However, research shows that candidates value honesty and are more likely to stay with companies that were upfront about both the good and the challenging aspects of working there.
              </p>
            </ExpandableCallout>

            <ExpandableCallout
              type="warning"
              title="Internal Resistance to Change"
              defaultOpen={true}
            >
              <p className="text-gray-300">
                Some team members might resist the shift to a more transparent employer brand, especially if they're used to traditional recruitment approaches. It's important to get buy-in from key stakeholders and demonstrate the long-term benefits of authenticity in hiring.
              </p>
            </ExpandableCallout>

            <ExpandableCallout
              type="warning"
              title="Balancing Transparency with Professionalism"
              defaultOpen={true}
            >
              <p className="text-gray-300">
                Finding the right balance between being transparent and maintaining professionalism can be challenging. The key is to be honest about challenges while also showing how you're addressing them and the positive aspects of your culture.
              </p>
            </ExpandableCallout>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmployerBrandKeyInfo; 